import * as CL from '@design-system/component-library';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../../../common/constants/dsClasses';
import { t } from '../../../../common/i18n';
import { Order } from '../../../../generated/api/order';
import type { PlaceOrderFormData } from '../../placeOrderTypes';

import './TotalOrPartialDeliveryButtons.scss';

interface TotalOrPartialDeliveryButtonsProps {
  isDirectDelivery?: boolean;
  hasBackorderedItems?: boolean;
  hasPreInstallation?: boolean;
  onErrorChange?: (hasError: boolean) => void;
}

export const TotalOrPartialDeliveryButtons = ({
  isDirectDelivery,
  hasBackorderedItems,
  hasPreInstallation,
  onErrorChange,
}: TotalOrPartialDeliveryButtonsProps) => {
  const { setValue, watch } = useFormContext<PlaceOrderFormData>();
  const shipmentType = watch('delivery.shipmentType');

  const handleShipmentTypeSelection = (type: Order.ShipmentTypeEnum) => {
    setValue('delivery.shipmentType', type);
    onErrorChange?.(false);
  };

  const totalDelivery = {
    id: Order.ShipmentTypeEnum.TOTAL_DELIVERY,
    name: t.EADV('Deliver all products in one batch'),
    icon: <span className="of-place-order-shipment-type__list--icon icon icon-total-delivery" />,
  };

  const partialDelivery = {
    id: Order.ShipmentTypeEnum.PARTIAL_DELIVERY,
    name: t.Q2QT('Allow partial delivery'),
    icon: <span className="of-place-order-shipment-type__list--icon icon icon-partial-delivery" />,
  };

  const getDeliveryOptions = () => {
    if (isDirectDelivery) {
      return [partialDelivery];
    }
    if (hasPreInstallation) {
      return [totalDelivery];
    }
    return [totalDelivery, partialDelivery];
  };

  return (
    <>
      {!isDirectDelivery && hasBackorderedItems && !hasPreInstallation && (
        <CL.Disclaimer
          disclaimerType="warning"
          icon={<CL.Icon icon="information" type="light" />}
          text={`${t.EE7J(
            'Your order contains products that are currently out of the stock. Please select between partial and one-batch delivery options.'
          )} ${t.UORO('Please select between partial and one-batch delivery options.')}`}
          visible
        />
      )}
      {isDirectDelivery && (
        <CL.Disclaimer
          disclaimerType="warning"
          icon={<CL.Icon icon="information" type="light" />}
          text={t.FX59(
            'One batch delivery is not possible, as some products in the cart are delivered from suppliers warehouse'
          )}
          visible
        />
      )}
      <CL.SelectGroup
        label={t.OOXW('Select delivery of products')}
        className={`of-place-order-shipment-type__list ${dsClass.MARGIN_TOP_3} ${dsClass.MARGIN_BOTTOM_3}`}
        products={getDeliveryOptions()}
        value={shipmentType}
        onChange={(id: string) => handleShipmentTypeSelection(id as Order.ShipmentTypeEnum)}
      />
      {shipmentType === Order.ShipmentTypeEnum.TOTAL_DELIVERY && (
        <p className={dsClass.TEXT_S}>{t.MPKA('Your order will be shipped as soons as all products are available.')}</p>
      )}
      {shipmentType === Order.ShipmentTypeEnum.PARTIAL_DELIVERY && hasBackorderedItems && !hasPreInstallation && (
        <p className={dsClass.TEXT_S}>
          {t.DQDK('Products will be delivered as soon as they are available. Delivery fee will be charged only once.')}
        </p>
      )}
    </>
  );
};
